
/**
 * WPS Common Utilities
 * 
 * Comprehensive shared utilities for WPS transaction components:
 * - General
 * - WeldingProcedure 
 * - WeldingProcedureSpec
 * 
 * Includes validation, submit logic, error handling, object creation utilities, and common styles
 */

import { Action, MessageTypes } from "../../utility/common/constants/CommonConstants";
import { emptyError as commonEmptyError } from "../../utility/common/TransactionComponents/TransactionValidationUtils";
import { handleWPSGeneralSubmit as handleGeneralSubmit, handleWPSGeneralSubmitTemp as handleGeneralSubmitTemp } from "../../utility/common/TransactionComponents/TransactionSubmitUtils";

// Re-export for backward compatibility
export { handleGeneralSubmit, handleGeneralSubmitTemp };

// Common Styles
export const bluestyle = {
  control: (baseStyles) => ({
    ...baseStyles,
    borderColor: "blue",
  }),
};

export const errorstyle = {
  control: (baseStyles) => ({
    ...baseStyles,
    borderColor: "red",
  }),
};

export const greenstyle = {
  control: (baseStyles) => ({
    ...baseStyles,
    borderColor: "green",
  }),
};

/**
 * Common validation logic for WPS forms
 */
// Error handling utilities (moved from WPS_common.js)

export const emptyError = (err, seterr, iserror) => {
  return commonEmptyError(err, seterr, iserror);
};




/**
 * WPS-specific process validation
 */
export const handleUnifiedSubmit = async ({
  isTemp = false,
  isEdited = true,
  validate,
  validate2 = null,
  handleAdd,
  setLoading,
  setIsEdited,
  setPageEdited,
  headerObj = null,
  detailObj = null,

  status = "Temporary Saved"
}) => {

  if (!isEdited) return MessageTypes.None


  if (!isEdited) return MessageTypes.None

  if (setPageEdited) setPageEdited(false);
  if (setIsEdited && !isTemp) setIsEdited(false);

  let validationResult;
  if (isTemp) {

    validationResult = await validate();
    if (validationResult.isError || validationResult.temperror) {
      return validationResult.isError ? MessageTypes.Required : MessageTypes.SpecialCharacters;
    }
    setLoading(true);
    const result = await handleAdd(status);
    setLoading(false);
    setIsEdited(false);

    if (result === true) return MessageTypes.Temp;
    if (result === MessageTypes.SpecialCharacters) return MessageTypes.SpecialCharacters;
    return MessageTypes.Fail;
  }
  else {
    // Handle dual validation for header submit
    
    if (validate2 && detailObj) {
      const detaildata = detailObj.filter((item) => item.WPSD_Process !== "");
      if (!(await validate()) && !(await validate2(detaildata))) {
        setLoading(true);
        const result = await handleAdd(status);
        setLoading(false);

        if (result === true) return MessageTypes.Success;
        if (result === MessageTypes.SpecialCharacters) return MessageTypes.SpecialCharacters;
        return MessageTypes.Fail;
      }
      return MessageTypes.RequiredAll;
    }

    const isError = await validate();
    if (isError) return MessageTypes.Required;

    setLoading(true);
    const result = await handleAdd(status || "");
    setLoading(false);

    if (result === true) {

      return MessageTypes.Success;
    }
    return result === MessageTypes.SpecialCharacters ? MessageTypes.SpecialCharacters : MessageTypes.Fail;
  }
};

export const handleInput = (e, validatorFn, fieldName, setError) => {
  const inputError = validatorFn(e.target.value);
  setError((prevState) => ({
    ...prevState,
    [fieldName]: inputError
  }));
  return inputError.status;
};

/**
 * Common onInput handler creator
 */
export const createInputHandler = (fieldName, validator, setError) => (e) =>
  handleInput(e, validator, fieldName, setError);

/**
 * Common error clear handler creator
 */
export const createErrorClearHandler = (fieldName, setError) => () => {
  setError((prevState) => ({
    ...prevState,
    [fieldName]: { status: false, message: "" }
  }));
};

/*** Common array change handler, where there are 3 fields example Filler metal size*/
export const handleArrayChange = (position, key, e, dataConfig, stateConfig, actionConfig, options = {}) => {
  const { row, col } = position;
  const { detailObj, setdetailObj } = dataConfig;
  const { setError } = stateConfig;
  const { action, setIsEdited, setIsEditedPage2 } = actionConfig;
  const { isEmpty = false, clearError = false } = options;
  
  if (action === Action.ActionEdit) setIsEdited(true);
  setIsEditedPage2(true);

  const temp = detailObj[row][key];
  temp[col] = isEmpty ? "" : (e?.target?.value || "");

  const onChangeVal = [...detailObj];
  onChangeVal[row][key] = temp;
  setdetailObj(onChangeVal);

  if (clearError) {
    setError((prevState) => ({
      ...prevState,
      [key]: { status: false, message: "" }
    }));
  }
};


/**
 * Common header change handler
 */
export const handleHeaderchange = (e, stateConfig, actionConfig, err) => {
  const { setheaderObj, setError } = stateConfig;
  const { action, setIsEdited, setEditedPage1, setEditedPage2, setEditedPage3 } = actionConfig;
  
  setIsEdited(true);

  if (action == Action.ActionEdit) {
    if (setEditedPage1 != undefined) setEditedPage1(true);
    if (setEditedPage2 != undefined) setEditedPage2(true);
    if (setEditedPage3 != undefined) setEditedPage3(true);
  }
  const { name, value } = e.target;
  setheaderObj((prevState) => ({
    ...prevState,
    [name]: value,
  }));

  if (err[name] && !String(err[name].message) == "Special")
    setError((prevState) => ({
      ...prevState,
      [name]: { status: false, message: "" },
    }));
};

/**
 * Common detail change handler
 */
export const handleDetailChange = (e, i, dataConfig, stateConfig, actionConfig, options = {}) => {
  const { detailObj, setdetailObj } = dataConfig;
  const { setError } = stateConfig;
  const { action, setIsEdited, setIsEditedPage2 } = actionConfig;
  const { clearError = false } = options;
  
  if (action === Action.ActionEdit) setIsEdited(true);
  setIsEditedPage2(true);

  const { name, value } = e.target;
  const onChangeVal = [...detailObj];
  onChangeVal[i][name] = value;
  setdetailObj(onChangeVal);

  if (clearError) {
    setError((prevState) => ({
      ...prevState,
      [name]: { status: false, message: "" }
    }));
  }
};

/**
 * Common handleCount function for WPS change number handling
 */
export const handleCount = (action, headerObj, location, Action, Status, AddNewCount) => {
  if (action === Action.ActionEdit) {
    if (headerObj.WPSH_Status === Status.StatusApproved) {
      headerObj.WPS_Change_No = location.state?.WPS_Change_No;
    }
  }
};

/**
 * Common error state initialization
 */
export const initializeErrorState = (pageType = 'all') => {
  const defaultError = { status: false, message: "" };

  const commonErrors = {
    WPSH_Project_No: { ...defaultError },
    WPSH_WPS_No: { ...defaultError },
    WPSH_WPS_Revi_No: { ...defaultError },
    WPS_QualifiedTo: { ...defaultError },
    WPSH_SupportingPQR: { ...defaultError },
    WPSH_WPS_Date: { ...defaultError },
    WPSH_RefDoc: { ...defaultError },
    WPSH_Scope: { ...defaultError },
    WPSH_JointDesc: { ...defaultError },
    WPSH_Notes: { ...defaultError },
    WPSH_PreparedBy_Date: { ...defaultError },
    WPSH_PreparedBy_Name: { ...defaultError },
    WPSH_ApprovedBy_Date: { ...defaultError },
    WPSH_ApprovedBy_Name: { ...defaultError },
    WPSH_PreparedBy_Remark: { ...defaultError },
    WPSH_ApprovedBy_Remark: { ...defaultError }
  };

  const page1Errors = {
    WPSH_BM_Type_Type: { ...defaultError },
    WPSH_BM_WeldingTo_Type: { ...defaultError },
    WPSH_BM_Type_GNo: { ...defaultError },
    WPSH_BM_Type_PNo: { ...defaultError },
    WPSH_BM_WeldingTo_PNo: { ...defaultError },
    WPSH_BM_WeldingTo_GNo: { ...defaultError },
    WPSH_BM_Backing_Backing: { ...defaultError },
    WPSH_BM_Backing_GNo: { ...defaultError },
    WPSH_BM_Backing_PNo: { ...defaultError },
    WPSH_BM_Retainer: { ...defaultError },
    WPSH_BM_Backing_Material: { ...defaultError },
    WPSH_BM_Notes: { ...defaultError },
    WPSH_MinOvlFinThick: { ...defaultError },
    WPSH_Flux_Details_WISLIP_CHECK: { ...defaultError },
    WPSH_BM_Type_HT_Con: { ...defaultError },
    WPSH_BM_WeldingTo_HT_Con: { ...defaultError },
    WPSH_FM_FluxType: { ...defaultError },
    WPSH_FM_FluxItemBatch: { ...defaultError },
    WPSH_FM_FluxFromRecrush: { ...defaultError },
    WPSH_FM_FluxItemCode: { ...defaultError },
    WPSH_FM_AlloyFlux: { ...defaultError },
    WPSH_FM_AlloyElement: { ...defaultError },
    WPSH_FM_Add_Del_Filler: { ...defaultError },
    WPSH_FM_Add_Del_Flux: { ...defaultError },
    WPSH_FM_Add_Del_orchange: { ...defaultError },
    WPSH_FM_NominalFlux: { ...defaultError },
    WPSH_FM_FilerMetal: { ...defaultError },
    WPSD_FM_Classification: { ...defaultError },
    WPSD_FM_ChemicalAnalysis_TradeName: { ...defaultError },
    WPSD_FM_TRQ_Min: { ...defaultError },
    WPSD_FM_TRQ_Max: { ...defaultError }
  };

  const page2Errors = {
    WPSD_WP_MinimumPreheatTemp: { ...defaultError },
    WPSD_WP_MaximumInterpassTemp: { ...defaultError },
    WPSD_WP_TungstenSIze: { ...defaultError },
    WPSD_WP_TungstenType: { ...defaultError },
    WPSD_WP_FillerMetalProductForm: { ...defaultError },
    WPSD_WP_WeldConsumable: { ...defaultError },
    WPSD_WP_FillerMetalSIze: { ...defaultError },
    WPSD_WP_LayerNumber: { ...defaultError },
    WPSD_WP_BatchNumber: { ...defaultError },
    WPSD_WP_PositionOfGroove: { ...defaultError },
    WPSD_WP_PositionOfOverlay: { ...defaultError },
    WPSD_WP_PositionOfFillet: { ...defaultError },
    WPSD_WP_WeldProgression: { ...defaultError },
    WPSD_WP_CurrentPolarity: { ...defaultError },
    WPSD_WP_Amperes: { ...defaultError },
    WPSD_WP_Amperes1: { ...defaultError },
    WPSD_WP_Pulsing_current: { ...defaultError },
    WPSD_WP_Volts: { ...defaultError },
    WPSD_WP_TravelSpeed: { ...defaultError },
    WPSD_WP_MaximumHeatInput: { ...defaultError },
    WPSD_WP_ShieldingGasType: { ...defaultError },
    WPSD_WP_ShieldingFlowRate: { ...defaultError },
    WPSD_WP_BackingFlowRate: { ...defaultError },
    WPSD_WP_BackingGasType: { ...defaultError },
    WPSD_WP_TrailingFlowRate: { ...defaultError },
    WPSD_WP_TrailingGasType: { ...defaultError },
    WPSD_WP_ArcTransferMode: { ...defaultError },
    WPSD_WP_StringOrWeave: { ...defaultError },
    WPSD_WP_Orifice_GasCupSize: { ...defaultError },
    WPSD_WP_CTWD: { ...defaultError },
    WPSD_WP_Multiple_SinglePassPerSide: { ...defaultError },
    WPSD_WP_Multiple_SingleLayer: { ...defaultError },
    WPSD_WP_Multi_SingleElectrode: { ...defaultError },
    WPSD_WP_ElectrodeSpacing_Late: { ...defaultError },
    WPSD_WP_ElectrodeSpacing_Logit: { ...defaultError },
    WPSD_WP_Max_Pass_Thickness: { ...defaultError },
    WPSD_WP_ElectrodeAngleDegree: { ...defaultError },
    WPSD_WP_Weld_Bead_Overlap: { ...defaultError },
    WPSD_WP_MinimumBeadLength: { ...defaultError },
    WPSD_WP_MinimumPreheatTemp_DataLogger: { ...defaultError },
    WPSD_WP_MaximumInterpassTemp_DataLogger: { ...defaultError },
    WPSD_WP_Maximumtravelspeed_DataLogger: { ...defaultError }
  };

  const page3Errors = {
    WPSH_PHT_ApplicationStd: { ...defaultError },
    WPSH_PHT_Desc: { ...defaultError },
    WPSH_PWHeatTre_Temperature: { ...defaultError },
    WPSH_PWHeatTre_Time: { ...defaultError },
    WPSH_PWHeatTre_Heating_Rate: { ...defaultError },
    WPSH_PWHeatTre_Heating_Method: { ...defaultError },
    WPSH_PWHeatTre_Cooling_Rate: { ...defaultError },
    WPSH_PWHeatTre_Cooling_Method: { ...defaultError },
    WPSH_PWHeatTre_Notes: { ...defaultError },
    WPSH_PWHeatTre_Weld_Notes: { ...defaultError },
    WPSH_PWHeatTre_Post_Heat_temp: { ...defaultError },
    WPSH_PWHeatTre_Post_Heat_time: { ...defaultError },
    WPSH_PWHeatTre_Wrap_insulation_material: { ...defaultError },
    WPSH_PHT_Root_spacing: { ...defaultError },
    WPSH_PWHeatTre_ISR_Temp: { ...defaultError },
    WPSH_PWHeatTre_ISR_Time: { ...defaultError },
    WPSH_Tech_Change_Electrode_dia: { ...defaultError },
    WPSH_Tech_BackGougingMethod: { ...defaultError },
    WPSH_Tech_ClosedtooutChamber: { ...defaultError },
    WPSH_Tech_GroovePreparation: { ...defaultError },
    WPSH_Tech_Inital_InterpassCleaning: { ...defaultError },
    WPSH_Tech_Peening: { ...defaultError },
    WPSH_Tech_SupplementaryMFControl: { ...defaultError },
    WPSH_Tech_SurfacePreparation: { ...defaultError },
    WPSH_Tech_UseOfThermalProcess: { ...defaultError },
    WPSH_Tech_Weld_deposit_chemistry: { ...defaultError },
    WPSH_Tech_Oscillation: { ...defaultError },
    WPSH_Tech_Oscillation_Width: { ...defaultError },
    WPSH_Tech_Oscillation_Freq: { ...defaultError },
    WPSH_Tech_Filler_Metal_Del: { ...defaultError }
  };

  switch (pageType) {
    case 'page1':
      return { ...commonErrors, ...page1Errors };
    case 'page2':
      return { ...commonErrors, ...page2Errors };
    case 'page3':
      return { ...commonErrors, ...page3Errors };
    case 'all':
    default:
      return { ...commonErrors, ...page1Errors, ...page2Errors, ...page3Errors };
  }

};



export const DisableFields = (actualStatus, preparedBy, Status, Action, action) => {
  if (action === Action.ActionCopy) {
    return true;
  } else if (actualStatus === Status.StatusPending && (sessionStorage.getItem("Band") !== "P" && sessionStorage.getItem("Band") !== "T")) {
    return true;
  } else {
    return false;
  }
};

export const createDetailObj = (start, WPStype) => {
  let temparray = [];
  for (let i = start; i <= 3; i++) {
    temparray.push({
      SK: "",
      PK: "",
      WPSH_Project_No: "",
      WPSH_WPS_No: "",
      WPSD_Process: "",
      WPSD_Type: "",
      WPSD_FM_SFA: "",
      WPSD_FM_FNo: "",
      WPSD_FM_Ano: "",
      WPSH_WeldLayer: "",
      WPSD_FM_Classification: "",
      WPSD_FM_ChemicalAnalysis_TradeName: "",
      WPSD_FM_TRQ_Max: "",
      WPSD_FM_TRQ_Min: "",
      wld_createdby: "",
      wld_createdon: "",
      wld_updatedby: "",
      wld_updatedon: "",
      WPSD_WP_MinimumPreheatTemp: "",
      WPSD_WP_MaximumInterpassTemp: "",
      WPSD_WP_TungstenSIze: "",
      WPSD_WP_TungstenType: "",
      WPSD_WP_FillerMetalProductForm: "",
      WPSD_WP_FillerMetalSIze: [],
      WPSD_WP_LayerNumber: [],
      WPSD_WP_PositionOfGroove: [],
      WPSD_WP_PositionOfFillet: [],
      WPSD_WP_WeldProgression: [],
      WPSD_WP_CurrentPolarity: [],
      WPSD_WP_Elec_char_others: "",
      WPSD_WP_Amperes: [],
      WPSD_WP_Amperes1: [],
      WPSD_WP_Pulsing_current: "",
      WPSD_WP_Volts: [],
      WPSD_WP_TravelSpeed: [],
      WPSD_WP_MaximumHeatInput: [],
      WPSD_WP_ShieldingFlowRate: "",
      WPSD_WP_ShieldingGasType: "",
      WPSD_WP_BackingGasType: "",
      WPSD_WP_BackingFlowRate: "",
      WPSD_WP_TrailingFlowRate: "",
      WPSD_WP_TrailingGasType: "",
      WPSD_WP_BatchNumber: [],
      WPSD_WP_WeldConsumable: [],
      WPSD_WP_PositionOfOverlay: [],
      WPSD_WP_Weld_Bead_Overlap: "",
      WPSD_WP_ArcTransferMode: "",
      WPSD_WP_StringOrWeave: "",
      WPSD_WP_Orifice_GasCupSize: "",
      WPSD_WP_CTWD: "",
      WPSD_WP_Multiple_SinglePassPerSide: "",
      WPSD_WP_Multiple_SingleLayer: "",
      WPSD_WP_Multi_SingleElectrode: "",
      WPSD_WP_ElectrodeSpacing_Late: "",
      WPSD_WP_ElectrodeSpacing_Logit: "",
      WPSD_WP_ElectrodeAngleDegree: "",
      WPSD_WP_Max_Pass_Thickness: "",
      status: "",
      wld_WPSType: WPStype,
      ActiveProcess: "Active",
      WPSH_WPS_Revi_No: "Active",
      WPSD_WP_WeldDesc: [],
      WPSD_WP_WeldWeight: [],
      welddesc: [],
      weldweight: [],
      WPSD_WP_MinimumBeadLength: [],
      WPSD_Flux_Details_WI_CHECK: false
    });
  }

  return temparray;
};

export const AddRowsDetailsoBj = (result, setdetailObj) => {
  let len = result.length;
  let loop = 3 - len;
  let temparray1 = [];

  if (loop > 0) {
    temparray1 = createDetailObj(len + 1);
    setdetailObj([...result, ...temparray1]);
  } else {
    setdetailObj([...result]);
  }
};
